import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  BrainCircuit,
  CheckCircle2,
  BarChart3,
  Trophy,
  MousePointerClick,
  Bone,
  Dumbbell,
  Activity,
  Move3d,
  ClipboardCheck,
  Stethoscope,
  Lock,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react';
import FunctionalAnatomyChallenge from './FunctionalAnatomyChallenge';

/* ------------------------------------------------------------------ */
/*  지식 챌린지 (Knowledge Challenge)                                    */
/*  원본 standalone HTML(Knowledge Challenge_main.html)의 히어로 +       */
/*  챌린지 카드 그리드를 그대로 재현. 자체 사이트 헤더/탭은 앱 전역        */
/*  헤더·탭바와 중복되어 생략, 화면폭 여백은 부모 페이지(HomePage) 패딩을 */
/*  그대로 상속.                                                        */
/* ------------------------------------------------------------------ */

type ChallengeCard = {
  key: string;
  icon: LucideIcon;
  tone: 'featured' | 'purple' | 'green' | 'orange' | 'rose' | 'sky';
  iconBg: string;
  iconColor: string;
  label: string;
  title: string;
  desc: string;
  open: boolean;
};

const CHALLENGES: ChallengeCard[] = [
  {
    key: 'functional-anatomy',
    icon: Bone,
    tone: 'featured',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-700',
    label: 'ANATOMY & KINESIOLOGY',
    title: '기능해부학 챌린지',
    desc: '뼈·관절·근육부터 기능해부학과 운동학까지 단계별로 지식을 확인합니다.',
    open: true,
  },
  {
    key: 'sports-instructor',
    icon: Dumbbell,
    tone: 'purple',
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-700',
    label: 'SPORTS INSTRUCTOR',
    title: '생활스포츠지도사 보디빌딩',
    desc: '생활스포츠지도사 보디빌딩 필기 영역을 중심으로 핵심 지식을 점검합니다.',
    open: false,
  },
  {
    key: 'exercise-physiology',
    icon: Activity,
    tone: 'green',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-700',
    label: 'EXERCISE PHYSIOLOGY',
    title: '운동생리학 챌린지',
    desc: '에너지 대사, 심폐 반응, 근신경 적응 등 운동생리학 핵심 개념에 도전합니다.',
    open: false,
  },
  {
    key: 'kinesiology',
    icon: Move3d,
    tone: 'orange',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-700',
    label: 'KINESIOLOGY',
    title: '운동학 챌린지',
    desc: '면과 축, 관절운동, 근육 작용과 움직임 분석 능력을 확인합니다.',
    open: false,
  },
  {
    key: 'exercise-prescription',
    icon: ClipboardCheck,
    tone: 'rose',
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-700',
    label: 'EXERCISE PRESCRIPTION',
    title: '운동처방 챌린지',
    desc: '운동강도, 빈도, 시간, 유형과 대상별 프로그램 설계 지식을 점검합니다.',
    open: false,
  },
  {
    key: 'pathophysiology',
    icon: Stethoscope,
    tone: 'sky',
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-700',
    label: 'PATHOPHYSIOLOGY',
    title: '병태생리학 챌린지',
    desc: '주요 질환의 기전과 운동 시 고려사항에 대한 기초 지식을 확인합니다.',
    open: false,
  },
];

const TONE_CARD_CLASS: Record<ChallengeCard['tone'], string> = {
  featured: 'bg-gradient-to-br from-[#F2F7FD] to-white border-[#C9DDF1]',
  purple: 'bg-gradient-to-br from-[#F7F5FF] to-white border-[#DED7FA]',
  green: 'bg-gradient-to-br from-[#F2FBF7] to-white border-[#CCEBDD]',
  orange: 'bg-gradient-to-br from-[#FFF8F1] to-white border-[#F4D7B8]',
  rose: 'bg-gradient-to-br from-[#FFF5F7] to-white border-[#F1D2DA]',
  sky: 'bg-gradient-to-br from-[#F2FAFD] to-white border-[#CDE7F0]',
};

const TONE_STATUS_CLASS: Record<ChallengeCard['tone'], string> = {
  featured: 'bg-blue-600 text-white',
  purple: 'bg-white border border-violet-200 text-violet-600',
  green: 'bg-white border border-emerald-200 text-emerald-600',
  orange: 'bg-white border border-orange-200 text-orange-600',
  rose: 'bg-white border border-rose-200 text-rose-600',
  sky: 'bg-white border border-sky-200 text-sky-600',
};

function Toast({ message }: { message: string | null }) {
  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] bg-gray-900 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg transition-all duration-200 ${
        message ? 'opacity-100 translate-y-0' : 'pointer-events-none opacity-0 translate-y-2'
      }`}
    >
      {message}
    </div>
  );
}

export default function KnowledgeChallenge() {
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeChallenge, setActiveChallenge] = useState<string | null>(null);

  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const showToast = (message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2200);
  };

  const handleCardClick = (card: ChallengeCard) => {
    if (!card.open) return;
    if (card.key === 'functional-anatomy') {
      setActiveChallenge(card.key);
      return;
    }
    showToast(`${card.title}는 곧 오픈됩니다. 조금만 기다려 주세요!`);
  };

  if (activeChallenge === 'functional-anatomy') {
    return <FunctionalAnatomyChallenge onBack={() => setActiveChallenge(null)} />;
  }

  return (
    <div>
      <style>{`
        .challenge-card { position: relative; overflow: hidden; transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease; box-shadow: 0 8px 24px rgba(30,58,95,.06); }
        .challenge-card:hover { transform: translateY(-4px); box-shadow: 0 16px 34px rgba(30,58,95,.13); }
        .challenge-card .icon-wrap { transition: transform .2s ease; }
        .challenge-card:hover .icon-wrap { transform: scale(1.06); }
        .challenge-card .arrow { transition: transform .2s ease; }
        .challenge-card:hover .arrow { transform: translateX(3px); }
        .challenge-card.locked { cursor: not-allowed; filter: grayscale(.2); box-shadow: none; }
        .challenge-card.locked:hover { transform: none; box-shadow: none; }
        .challenge-card.locked > *:not(.locked-overlay) { opacity: .24; }
        .locked-overlay { position: absolute; inset: 0; z-index: 5; display: flex; align-items: center; justify-content: center; pointer-events: none; }
        .locked-badge { position: relative; z-index: 1; display: flex; align-items: center; gap: 8px; padding: 8px 14px; border: 1px solid #CBD5E1; border-radius: 9999px; background: rgba(255,255,255,.94); color: #64748B; font-size: 13px; font-weight: 800; box-shadow: 0 4px 12px rgba(15,23,42,.06); }
        .challenge-hero { background: linear-gradient(135deg,#173B67 0%,#244F7E 55%,#32689E 100%); position: relative; overflow: hidden; }
        .challenge-hero:before { content: ""; position: absolute; width: 360px; height: 360px; border-radius: 50%; background: rgba(255,255,255,.06); right: -100px; top: -180px; }
        .challenge-hero:after { content: ""; position: absolute; width: 220px; height: 220px; border-radius: 50%; background: rgba(245,130,31,.16); right: 180px; bottom: -150px; }
      `}</style>

      {/* 히어로 */}
      <section className="challenge-hero rounded-3xl px-5 sm:px-7 lg:px-10 py-8 sm:py-9 lg:py-11 mb-8 text-white shadow-soft">
        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.16em] uppercase text-blue-100 mb-3">
            <BrainCircuit className="w-4 h-4" /> Knowledge Challenge
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">지식 챌린지</h1>
          <p className="mt-3 text-sm sm:text-base leading-7 text-blue-100">
            배운 지식을 직접 확인하고, 분야별 챌린지에 도전해 보세요.
            <br className="hidden sm:block" />
            문제를 풀며 현재 수준을 확인하고 한 단계 더 성장할 수 있습니다.
          </p>
          <div className="mt-6 flex flex-wrap gap-2 text-xs font-semibold">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> 실력 점검
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1.5">
              <BarChart3 className="w-3.5 h-3.5" /> 단계별 도전
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1.5">
              <Trophy className="w-3.5 h-3.5" /> 학습 성취
            </span>
          </div>
        </div>
      </section>

      {/* 섹션 헤더 */}
      <section className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-[#173B67]">챌린지를 선택하세요</h2>
          <p className="mt-1.5 text-xs text-gray-500">관심 있는 분야를 선택하면 해당 지식 챌린지로 이동합니다.</p>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-gray-400">
          <MousePointerClick className="w-4 h-4" /> 카드를 클릭해 시작
        </span>
      </section>

      {/* 챌린지 카드 그리드 */}
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 pb-12">
        {CHALLENGES.map((card) => {
          const Icon = card.icon;
          return (
            <button
              key={card.key}
              type="button"
              onClick={() => handleCardClick(card)}
              aria-disabled={!card.open}
              className={`challenge-card ${!card.open ? 'locked' : ''} min-h-[238px] rounded-2xl border p-6 flex flex-col text-left group ${TONE_CARD_CLASS[card.tone]}`}
            >
              <div className="flex items-start justify-between">
                <span className={`icon-wrap w-14 h-14 rounded-2xl flex items-center justify-center ${card.iconBg} ${card.iconColor}`}>
                  <Icon className="w-7 h-7" />
                </span>
                <span className={`status-pill text-[11px] font-extrabold tracking-wide rounded-full px-2.5 py-1 ${TONE_STATUS_CLASS[card.tone]}`}>
                  {card.open ? 'OPEN' : 'COMING SOON'}
                </span>
              </div>
              <div className="mt-5">
                <p className={`text-xs font-bold tracking-wider ${card.iconColor}`}>{card.label}</p>
                <h3 className="mt-1.5 text-xl font-extrabold text-gray-900">{card.title}</h3>
                <p className="mt-2 text-sm leading-6 text-gray-500">{card.desc}</p>
              </div>
              {card.open && (
                <div className="mt-auto pt-4 flex items-center justify-between text-sm font-bold text-blue-700">
                  <span>도전하기</span>
                  <ArrowRight className="arrow w-4 h-4" />
                </div>
              )}
              {!card.open && (
                <div className="locked-overlay">
                  <span className="locked-badge">
                    <Lock className="w-4 h-4" /> 준비 중
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </section>

      <Toast message={toast} />
    </div>
  );
}
